import { PanelText } from "./PanelText";
import { SURFACE, INK, RO } from "../theme3d";
import { usePanelOccluded } from "./PanelOcclusionContext";

export interface GlyphChipProps {
  label: string;
  selected?: boolean;
  position?: [number, number, number];
  anchorX?: "left" | "center" | "right";
  occluded?: boolean;
}

const CHIP_HEIGHT = 0.052;
const PAD_X = 0.028;
const BORDER_MARGIN = 0.004;

// A rounded-token-style chip for a controller glyph (A/X, B/Y, TRIGGER),
// kept as a separate element beside/under the action word — never baked
// into the label text. Fixes the "B / Y Campaign" defect
// (HANDOFF-main-menu-visual-redesign.md §4d): the glyph is an accessory to
// the action, not part of the headline.
export function GlyphChip({
  label,
  selected = false,
  position = [0, 0, 0],
  anchorX = "center",
  occluded,
}: GlyphChipProps) {
  const resolvedOccluded = usePanelOccluded(occluded);
  const [x, y, z] = position;
  // Rough width from character count — the pill just needs to comfortably
  // contain the centered label, not fit it pixel-exact.
  const width = Math.max(0.08, label.length * 0.019 + PAD_X * 2);
  const offsetX = anchorX === "left" ? width / 2 : anchorX === "right" ? -width / 2 : 0;
  const renderOrder = resolvedOccluded ? RO.panelChip : RO.overlayBacking + 1;

  return (
    <group position={[x + offsetX, y, z]}>
      <mesh renderOrder={renderOrder}>
        <planeGeometry args={[width + BORDER_MARGIN, CHIP_HEIGHT + BORDER_MARGIN]} />
        <meshBasicMaterial
          color={selected ? SURFACE.chipBorderSelected : SURFACE.chipBorder}
          transparent
          opacity={0.9}
          depthTest={resolvedOccluded}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, 0, 0.001]} renderOrder={renderOrder}>
        <planeGeometry args={[width, CHIP_HEIGHT]} />
        <meshBasicMaterial
          color={selected ? SURFACE.chipFillSelected : SURFACE.chipFill}
          transparent
          opacity={0.88}
          depthTest={resolvedOccluded}
          depthWrite={false}
        />
      </mesh>
      <PanelText tier="chip" color={selected ? INK.title : INK.body} occluded={occluded}>
        {label}
      </PanelText>
    </group>
  );
}
